import { useState } from "react";
import { 
  Upload, 
  Download, 
  Eye, 
  Trash2, 
  MoreVertical,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Plus,
  LucideIcon,
  Unlink,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export type DocumentStatus = "valid" | "expiring" | "expired" | "missing";

export interface DocumentChecklistCardProps {
  /** Document type key */
  typeKey: string;
  /** Display label */
  label: string;
  /** Short description */
  description: string;
  /** Icon component */
  icon: LucideIcon;
  /** Is this document required? */
  required?: boolean;
  /** Is this an essential document (for score)? */
  isEssential?: boolean;
  /** Current status */
  status: DocumentStatus;
  /** Status label to display */
  statusLabel: string;
  /** Linked document data (if any) */
  linkedDocument?: {
    id: string;
    title: string;
    file_name: string;
    file_path: string;
  } | null;
  /** Expiration date (if any) */
  expiresAt?: string | null;
  /** Age warning for short-lived documents (justificatifs < 3 mois) */
  ageWarning?: { months: number };
  /** Called when user wants to link/upload a document */
  onLink: () => void;
  /** Called when user wants to view the document */
  onView?: (documentId: string) => void;
  /** Called when user unlinks the document */
  onUnlink?: () => void;
  /** Called after any action that modifies data */
  onRefresh?: () => void;
  /** Table to delete from (for delete action) */
  deleteTable?: "legal_documents" | "essential_doc_links";
  /** ID of the link record to delete */
  linkRecordId?: string;
}

const STATUS_CONFIG: Record<DocumentStatus, {
  icon: LucideIcon;
  color: string;
  bgColor: string;
  borderColor: string;
  badgeClass: string;
}> = {
  valid: {
    icon: CheckCircle2,
    color: "text-emerald-600",
    bgColor: "bg-emerald-500/10",
    borderColor: "border-emerald-500/20",
    badgeClass: "border-emerald-500/30 text-emerald-600 bg-emerald-500/10",
  },
  expiring: {
    icon: Clock,
    color: "text-amber-600",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/20",
    badgeClass: "border-amber-500/30 text-amber-600 bg-amber-500/10",
  },
  expired: {
    icon: AlertTriangle,
    color: "text-destructive",
    bgColor: "bg-destructive/10",
    borderColor: "border-destructive/20",
    badgeClass: "border-destructive/30 text-destructive bg-destructive/10",
  },
  missing: {
    icon: Plus,
    color: "text-muted-foreground",
    bgColor: "bg-muted",
    borderColor: "border-muted",
    badgeClass: "bg-muted text-muted-foreground",
  },
};

const DocumentChecklistCard = ({
  typeKey,
  label,
  description,
  icon: IconComponent,
  required = false,
  isEssential = false,
  status,
  statusLabel,
  linkedDocument,
  expiresAt,
  ageWarning,
  onLink,
  onView,
  onUnlink,
  onRefresh,
  deleteTable,
  linkRecordId,
}: DocumentChecklistCardProps) => {
  const [loading, setLoading] = useState(false);
  const config = STATUS_CONFIG[status];
  const StatusIcon = config.icon;
  const hasDocument = !!linkedDocument?.file_path;

  const handleDownload = async () => {
    if (!linkedDocument?.file_path) return;

    try {
      setLoading(true);
      const { data, error } = await supabase.storage
        .from("documents")
        .download(linkedDocument.file_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = linkedDocument.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le document",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleView = () => {
    if (linkedDocument?.id && onView) {
      onView(linkedDocument.id);
    }
  };

  const handleDelete = async () => {
    if (!linkRecordId || !deleteTable) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from(deleteTable)
        .delete()
        .eq("id", linkRecordId);

      if (error) throw error;

      toast({
        title: "Document supprimé",
        description: "Le lien a été supprimé",
      });
      onRefresh?.();
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le document",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = () => {
    if (hasDocument && onView) {
      handleView();
    }
  };

  return (
    <Card 
      className={cn(
        "transition-all hover:shadow-md",
        status === "missing" && "border-dashed",
        status === "expired" && "border-destructive/50",
        status === "expiring" && "border-amber-500/50",
        hasDocument && "cursor-pointer"
      )}
      onClick={hasDocument ? handleCardClick : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={cn("p-3 rounded-xl shrink-0", config.bgColor)}>
            <IconComponent className={cn("w-6 h-6", config.color)} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-medium">{label}</h3>
                  {isEssential && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-primary/50 text-primary bg-primary/5">
                      <Star className="w-2.5 h-2.5 mr-0.5 fill-primary" />
                      Essentiel
                    </Badge>
                  )}
                  {required && !isEssential && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                      Requis
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-1">
                  {description}
                </p>
              </div>
              
              {/* Actions */}
              {hasDocument ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="shrink-0 h-8 w-8"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {onView && (
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleView(); }} disabled={loading}>
                        <Eye className="w-4 h-4 mr-2" />
                        Voir
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDownload(); }} disabled={loading}>
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onLink(); }}>
                      <Upload className="w-4 h-4 mr-2" />
                      Remplacer
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {onUnlink && (
                      <DropdownMenuItem 
                        onClick={(e) => { e.stopPropagation(); onUnlink(); }} 
                        disabled={loading}
                        className="text-destructive"
                      >
                        <Unlink className="w-4 h-4 mr-2" />
                        Délier
                      </DropdownMenuItem>
                    )}
                    {deleteTable && linkRecordId && (
                      <DropdownMenuItem 
                        onClick={(e) => { e.stopPropagation(); handleDelete(); }} 
                        disabled={loading}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={(e) => { e.stopPropagation(); onLink(); }}
                  className="shrink-0"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              )}
            </div>

            {/* Status Badge */}
            <div className="mt-3 flex items-center gap-2">
              <Badge 
                variant="outline" 
                className={cn("gap-1", config.badgeClass)}
              >
                <StatusIcon className="w-3 h-3" />
                {statusLabel}
              </Badge>
              
              {linkedDocument?.file_name && (
                <span className="text-xs text-muted-foreground truncate">
                  {linkedDocument.file_name}
                </span>
              )}
            </div>

            {/* Expiration or age warning info */}
            {ageWarning && ageWarning.months >= 2 && (
              <p className={cn(
                "text-xs mt-2",
                ageWarning.months >= 3 ? "text-destructive font-medium" : "text-amber-600"
              )}>
                ⚠️ Document de {ageWarning.months} mois (max. 3 mois)
              </p>
            )}
            {expiresAt && !ageWarning && (
              <p className="text-xs text-muted-foreground mt-2">
                Expiration : {format(new Date(expiresAt), "d MMMM yyyy", { locale: fr })}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DocumentChecklistCard;
